import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { listAdmins, createAdmin, deleteAdmin, changePassword } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BrandedHeader from "@/components/BrandedHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AdminSideNav } from "@/pages/admin/AdminDashboardPage";
import { Users, Plus, Trash2, Loader2, Shield, Mail, Key, Lock, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";

export default function AdminSettingsPage() {
  const navigate = useNavigate();
  const { admin: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  
  // Add admin form
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [adding, setAdding] = useState(false);
  
  // Change password form
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPasswordChange, setNewPasswordChange] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      const res = await listAdmins();
      setAdmins(res.data);
    } catch (e) {
      toast.error("Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newEmail || !newPassword) {
      toast.error("Email and password are required");
      return;
    }

    setAdding(true);
    try {
      await createAdmin({
        email: newEmail,
        password: newPassword,
        is_super_admin: isSuperAdmin
      });
      
      toast.success(`Admin ${newEmail} created successfully`);
      setAddDialogOpen(false);
      setNewEmail("");
      setNewPassword("");
      setIsSuperAdmin(false);
      loadAdmins();
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to create admin";
      toast.error(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteAdmin = async (adminId, email) => {
    if (!confirm(`Are you sure you want to delete admin ${email}?`)) return;

    try {
      await deleteAdmin(adminId);
      toast.success(`Admin ${email} deleted`);
      loadAdmins();
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to delete admin";
      toast.error(msg);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPasswordChange || !confirmPassword) {
      toast.error("All fields are required");
      return;
    }

    if (newPasswordChange !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (newPasswordChange.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPasswordChange);
      toast.success("Password changed successfully");
      setPasswordDialogOpen(false);
      setCurrentPassword("");
      setNewPasswordChange("");
      setConfirmPassword("");
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to change password";
      toast.error(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <AdminSideNav active="Settings" />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-semibold">Settings</h1>
            <p className="text-sm text-muted-foreground">Manage admins and account settings</p>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-5xl mx-auto space-y-6">
            
            {/* Account Settings */}
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Account Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Change Password</p>
                    <p className="text-xs text-muted-foreground">Update your account password</p>
                  </div>
                  <Button variant="outline" onClick={() => setPasswordDialogOpen(true)}>
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                </div>
                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <p className="text-sm font-medium">System Configuration</p>
                    <p className="text-xs text-muted-foreground">
                      LLM provider keys, email (O365 SMTP), and proctoring policy
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/admin/system-settings")}
                    data-testid="open-system-settings-btn"
                  >
                    <SettingsIcon className="w-4 h-4 mr-2" />
                    Open System Settings
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Admin Management */}
            <Card className="border-border bg-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-display flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Admin Users
                </CardTitle>
                <Button onClick={() => setAddDialogOpen(true)} size="sm" className="btn-press">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Admin
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 text-primary animate-spin mx-auto" />
                  </div>
                ) : admins.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No admins found</p>
                ) : (
                  <div className="space-y-3">
                    {admins.map(admin => (
                      <div key={admin.id} className="flex items-center justify-between p-4 rounded-lg border border-border bg-card/50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{admin.email}</p>
                            <p className="text-xs text-muted-foreground">
                              Created {new Date(admin.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {admin.is_super_admin && (
                            <Badge variant="secondary" className="ml-2">
                              <Shield className="w-3 h-3 mr-1" />
                              Super Admin
                            </Badge>
                          )}
                          {admin.email === currentAdmin?.email && (
                            <Badge variant="outline" className="ml-2">You</Badge>
                          )}
                        </div>
                        
                        {!admin.is_super_admin && admin.email !== currentAdmin?.email && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeleteAdmin(admin.id, admin.email)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Admin Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Add New Admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Email Address</label>
              <Input
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="admin@example.com"
                data-testid="add-admin-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Password</label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Enter password"
                data-testid="add-admin-password"
              />
              <p className="text-xs text-muted-foreground mt-1">Minimum 8 characters</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="super-admin-check"
                checked={isSuperAdmin}
                onChange={e => setIsSuperAdmin(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="super-admin-check" className="text-sm">
                Grant super admin privileges
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddAdmin} disabled={adding} className="btn-press" data-testid="add-admin-submit">
              {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Current Password</label>
              <Input
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">New Password</label>
              <Input
                type="password"
                value={newPasswordChange}
                onChange={e => setNewPasswordChange(e.target.value)}
                placeholder="Enter new password"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Confirm New Password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleChangePassword} disabled={changingPassword} className="btn-press">
              {changingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
